import type { ContentPart } from "@qn/providers";
import { cn } from "@qn/ui";
import { ChevronRight, Loader2 } from "lucide-react";
import type { ArtifactType } from "../lib/artifacts";
import { useT } from "../lib/i18n";
import { useUI } from "../lib/ui-store";
import type { ToolState } from "./AgentParts";
import { TYPE_ICON } from "./ArtifactPanel";

/** Inline card in the chat for create_artifact / update_artifact calls; click opens the side panel. */
export function ArtifactCard({
  call,
  result,
  state,
}: {
  call: Extract<ContentPart, { type: "tool_call" }>;
  result?: string;
  state: ToolState;
}) {
  const t = useT();
  const input = (call.input ?? {}) as Record<string, unknown>;
  const id =
    call.name === "update_artifact"
      ? String(input.id ?? "")
      : (/id ([0-9a-f-]{36})/.exec(result ?? "")?.[1] ?? "");
  const type =
    (String(input.type ?? "document") as ArtifactType) in TYPE_ICON
      ? (String(input.type) as ArtifactType)
      : "document";
  const Icon = TYPE_ICON[type];
  const failed = state === "error";
  return (
    <button
      type="button"
      disabled={!id || failed}
      onClick={() => useUI.getState().openArtifact(id)}
      className={cn(
        "my-2 flex w-full items-center gap-3 rounded-xl border border-[var(--line)] bg-[var(--surface-2)]/60 px-3.5 py-3 text-start transition",
        id && !failed
          ? "hover:border-[var(--accent-ring)] hover:bg-[var(--surface-2)]"
          : "opacity-70",
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent-text)]">
        {state === "running" ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Icon className="size-4" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium" dir="auto">
          {String(
            input.title ??
              (call.name === "update_artifact" ? t("artifact.updated") : t("artifact.untitled")),
          )}
        </span>
        <span className="block text-xs text-[var(--fg-faint)]">
          {failed
            ? t("artifact.failed")
            : call.name === "update_artifact"
              ? t("artifact.updated")
              : t(`artifact.type.${type}`)}
        </span>
      </span>
      {id && !failed && <ChevronRight className="size-4 text-[var(--fg-faint)] rtl:-scale-x-100" />}
    </button>
  );
}
